import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { blue, grey } from '@mui/material/colors';
import { MdCheckCircle, MdClose } from '@teamworksdev/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GalleryViewMode } from '../../../components/gallery/types';
import { AdvancedSearchGallery } from './AdvancedSearchGallery';

type SelectableItem = {
  id: string;
};

export const AdvancedSearchModal = <T extends SelectableItem>({
  id,
  modalTitle,
  galleryTitle,
  titleActions,
  items,
  filters,
  open,
  onClose,
  onAddItem,
  renderCard,
  renderTableView,
  isLoading,
  isError,
}: {
  id: string;
  modalTitle: string;
  galleryTitle: string;
  titleActions?: React.ReactNode;
  open: boolean;
  items: T[];
  filters: React.ReactNode;
  onClose: () => void;
  onAddItem: (items: T) => void;
  renderCard?: (
    item: T,
    viewMode: GalleryViewMode,
    isSelected: boolean,
    setSelected: (item: T) => void,
  ) => React.ReactNode;
  renderTableView: (
    items: T[],
    selectedItem: T | undefined,
    setSelected: (item: T) => void,
  ) => React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
}) => {
  const [selected, setSelected] = useState<T | undefined>(undefined);
  const { t } = useTranslation();

  // Reset the selection when the modal closes so a reopened modal doesn't
  // carry a stale item from a previous session
  useEffect(() => {
    if (!open) {
      setSelected(undefined);
    }
  }, [open]);

  // The captured `selected` object can go stale if the underlying query
  // refetches (e.g. a template was edited after selection) — resolve the
  // current record by id at Add time instead of passing the old snapshot
  const selectedCurrent = selected
    ? items.find((item) => item.id === selected.id)
    : undefined;

  const onClear = () => {
    setSelected(undefined);
  };

  return (
    <Dialog key={id} onClose={onClose} open={open} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ borderBottom: 2, borderColor: grey[200] }}>
        {modalTitle}
      </DialogTitle>
      <Box
        sx={{
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          top: 12,
          right: 8,
        }}
      >
        {titleActions}
        <IconButton onClick={onClose} aria-label={t('close')}>
          <MdClose size={24} />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          backgroundColor: grey[50],
          p: 0,
          overflow: 'hidden',
          height: 800,
        }}
      >
        <AdvancedSearchGallery
          key={id}
          title={galleryTitle}
          items={items}
          isLoading={isLoading}
          isError={isError}
          renderCard={
            renderCard
              ? ({ item, viewMode }) => {
                  const isSelected = !!(selected && selected.id === item.id);
                  return (
                    <div className="relative inline">
                      {isSelected && (
                        <MdCheckCircle
                          color={blue[600]}
                          size={18}
                          className="absolute top-6 right-6"
                        />
                      )}
                      {renderCard(item, viewMode, isSelected, setSelected)}
                    </div>
                  );
                }
              : null
          }
          renderTableView={({ items }) =>
            renderTableView(items, selected, setSelected)
          }
          filterSection={<div className="flex flex-col gap-3">{filters}</div>}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={{ ml: 1 }} onClick={onClear}>
          {t('clear')}
        </Button>
        <Button onClick={() => selectedCurrent && onAddItem(selectedCurrent)}>
          {t('add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
